import React, { useState } from 'react';
import { Flex, Spinner, Text } from '@zillow/constellation';
import { useDispatch } from 'react-redux';

import AutocompleteAreaInput from 'app/shared/components/AutocompleteAreaInput';
import DataCard from 'app/shared/components/DataCard';

import * as S from './styles';
import { augmentError, formatForLog } from 'app/shared/utils/errors';
import api from 'app/shared/utils/api';
import numberUtils from 'app/shared/utils/numberUtils';
import { getCompareRentQuery } from 'app/shared/utils/queries';
import { trackEvent } from 'app/shared/utils/analytics/trackEvent';
import { gaEvents } from 'app/shared/constants/AnalyticsConstants';

import type AutocompleteArea from 'app/shared/models/AutocompleteArea';
import type { Market } from 'app/shared/types/Market';

interface CompareRentProps {
    market: Market;
    medianNationalRent: number | null;
    isSmallScreen: boolean;
    isMobile: boolean;
}

const logger = log.child('components/CompareRent');

const CompareRent: React.FunctionComponent<CompareRentProps> = (props) => {
    const { market, medianNationalRent, isSmallScreen, isMobile } = props;
    const dispatch = useDispatch();
    const [otherMarket, setOtherMarket] = useState<Market>({
        name: 'the national median',
        medianRent: medianNationalRent || 0,
        resourceId: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const rentMultiplier = market.medianRent / otherMarket.medianRent - 1;
    const percentDifference = Math.abs(rentMultiplier * 100);
    const displayPercentage =
        percentDifference < 1
            ? numberUtils.asPercent(percentDifference, '0.00%')
            : numberUtils.asPercent(percentDifference);

    const submit = (area: AutocompleteArea) => {
        async function updateOtherMarket() {
            if (area.areaId) {
                setLoading(true);
                setError(null);
                const resourceId = market.resourceId;
                const otherResourceId = area.resourceId || '';
                // @ts-ignore
                const { data, errors } = await dispatch(
                    api.area.graphQlMarketData(getCompareRentQuery({ resourceId, otherResourceId }))
                );

                if (errors.length > 0) {
                    setLoading(false);
                    setError('An error occurred. Please try again.');
                    throw new Error(errors[0].code);
                }

                setOtherMarket({
                    name: area.longName || '',
                    medianRent: data?.marketPage?.rentCompare?.medianRent ?? 0,
                    resourceId: otherResourceId || ''
                });
                setLoading(false);

                if (otherMarket.medianRent !== 0) {
                    trackEvent({ ...gaEvents.COMPARE_TO_MARKET, label: otherResourceId });
                } else {
                    trackEvent({ ...gaEvents.COMPARE_TO_MARKET_NO_DATA, label: area.resourceId || 'EMPTY' });
                }
            }
        }

        try {
            updateOtherMarket();
        } catch (err) {
            augmentError(err, {
                root: 'components/CompareRent#submit'
            });
            logger.error(formatForLog(err), `Compare Rent: error fetching median rent for ${area.longName}`);
        }
    };

    return (
        <DataCard
            heading="Compare Median Rent in Markets"
            copyText={
                otherMarket.medianRent === 0 ? (
                    `Data is not available for ${otherMarket.name}.`
                ) : otherMarket.medianRent === market.medianRent ? (
                    `Rent in ${market.name} is equal to rent in ${otherMarket.name}.`
                ) : (
                    <>
                        Rent in {market.name} is <strong>{displayPercentage}</strong>{' '}
                        {rentMultiplier >= 0 ? 'higher' : 'lower'} than {otherMarket.name}.
                    </>
                )
            }
            useFullScreen={isSmallScreen}
        >
            <S.Stripe display="flex" justifyContent="center" flexDirection="row" marginTop="sm">
                <S.MarketName fontType={isMobile ? 'bodySmallHeading' : 'bodyHeading'}>{market.name}</S.MarketName>
                <S.Versus fontType={isMobile ? 'bodySmallHeading' : 'bodyHeading'}>vs.</S.Versus>
                <S.AutocompleteContainer padding="xs">
                    <AutocompleteAreaInput
                        submit={submit}
                        type="secondary"
                        error={error}
                        blockedAreas={[market.resourceId]}
                        placeholder="Search"
                    />
                </S.AutocompleteContainer>
            </S.Stripe>
            <Flex display="flex" alignItems="center" justifyContent="space-evenly" flexDirection="row" flex="1">
                <S.RentPrice fontType="h2" level="2" fontFamily="serif" fontColor="blue500">
                    {numberUtils.asCurrency(market.medianRent)}
                </S.RentPrice>
                <S.OtherRentPrice fontType="h2" level="2" fontFamily="serif" fontColor="aqua400">
                    {loading ? (
                        <Spinner />
                    ) : otherMarket.medianRent !== 0 ? (
                        numberUtils.asCurrency(otherMarket.medianRent)
                    ) : (
                        <>
                            --
                            <Text as="p" fontType="bodySmall" fontColor="coolGray500" paddingX="md">
                                Data is not available. Please try a different market.
                            </Text>
                        </>
                    )}
                </S.OtherRentPrice>
            </Flex>
        </DataCard>
    );
};

export default CompareRent;
